/**
 * User detail page - FE-04.
 * Profile info, account actions, tabs: Overview / Jobs / Audit / Billing.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  type UserLifecycleAction,
  type UserLifecyclePayload,
  runUserLifecycleAction,
} from "../../api/admin-user-lifecycle";
import {
  type AdminUserDetail,
  fetchUserDetail,
  freezeUser,
  unfreezeUser,
} from "../../api/admin-users";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";
import { type UserAction, UserActionButtons } from "./UserDetailActions";
import {
  InfoCard,
  type UserDetailTab,
  UserDetailTabBar,
  UserDetailTabContent,
} from "./UserDetailContent";
import { UserLifecycleDialog } from "./UserLifecycleDialog";
import { s } from "./user-detail-styles";

type SimpleUserAction = "freeze" | "unfreeze";

export function UserDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { user, loading, error, setError, load } = useUserDetailLoader(id);
  const [tab, setTab] = useState<UserDetailTab>("Overview");
  const [dialog, setDialog] = useState<UserAction | null>(null);
  const { handleLifecycleConfirm, handleSimpleConfirm } = useUserActionHandlers(
    { id, dialog, load, setDialog, setError },
  );

  if (loading) return <p style={s.msg}>{t("user_detail.loading")}</p>;
  if (error)
    return <p style={{ ...s.msg, color: "var(--color-danger)" }}>{error}</p>;
  if (!user) return <p style={s.msg}>{t("user_detail.not_found")}</p>;

  return (
    <LoadedUserDetail
      user={user}
      tab={tab}
      dialog={dialog}
      onTabChange={setTab}
      onDialogChange={setDialog}
      onSimpleConfirm={(reason) => void handleSimpleConfirm(reason)}
      onLifecycleConfirm={(payload) => void handleLifecycleConfirm(payload)}
    />
  );
}

function useUserDetailLoader(id: string | undefined): {
  readonly user: AdminUserDetail | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly setError: (error: string | null) => void;
  readonly load: () => Promise<void>;
} {
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setUser(await fetchUserDetail(id));
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t("user_detail.failed_to_load"),
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  return { user, loading, error, setError, load };
}

function useUserActionHandlers({
  id,
  dialog,
  load,
  setDialog,
  setError,
}: {
  readonly id: string | undefined;
  readonly dialog: UserAction | null;
  readonly load: () => Promise<void>;
  readonly setDialog: (dialog: UserAction | null) => void;
  readonly setError: (error: string | null) => void;
}): {
  readonly handleSimpleConfirm: (reason: string) => Promise<void>;
  readonly handleLifecycleConfirm: (
    payload: UserLifecyclePayload,
  ) => Promise<void>;
} {
  const handleSimpleConfirm = useCallback(
    async (reason: string): Promise<void> => {
      if (!id || !isSimpleAction(dialog)) return;
      try {
        if (dialog === "freeze") await freezeUser(id, reason);
        else await unfreezeUser(id, reason);
        setDialog(null);
        void load();
      } catch (e) {
        setError(e instanceof Error ? e.message : t("common.action_failed"));
        setDialog(null);
      }
    },
    [id, dialog, load, setDialog, setError],
  );

  const handleLifecycleConfirm = useCallback(
    async (payload: UserLifecyclePayload): Promise<void> => {
      if (!id || !isLifecycleAction(dialog)) return;
      try {
        await runUserLifecycleAction(id, dialog, payload);
        setDialog(null);
        void load();
      } catch (e) {
        setError(e instanceof Error ? e.message : t("common.action_failed"));
        setDialog(null);
      }
    },
    [id, dialog, load, setDialog, setError],
  );

  return { handleSimpleConfirm, handleLifecycleConfirm };
}

function LoadedUserDetail({
  user,
  tab,
  dialog,
  onTabChange,
  onDialogChange,
  onSimpleConfirm,
  onLifecycleConfirm,
}: {
  readonly user: AdminUserDetail;
  readonly tab: UserDetailTab;
  readonly dialog: UserAction | null;
  readonly onTabChange: (tab: UserDetailTab) => void;
  readonly onDialogChange: (dialog: UserAction | null) => void;
  readonly onSimpleConfirm: (reason: string) => void;
  readonly onLifecycleConfirm: (payload: UserLifecyclePayload) => void;
}): React.JSX.Element {
  const simpleAction = isSimpleAction(dialog) ? dialog : null;
  const lifecycleAction = isLifecycleAction(dialog) ? dialog : null;

  return (
    <div>
      <PageHeader
        title={user.display_name}
        description={user.email}
        actions={
          <UserActionButtons status={user.status} onAction={onDialogChange} />
        }
      />
      <InfoCard user={user} />
      <UserDetailTabBar active={tab} onChange={onTabChange} />
      <UserDetailTabContent tab={tab} user={user} />
      <UserDetailDialogs
        userName={user.display_name}
        simpleAction={simpleAction}
        lifecycleAction={lifecycleAction}
        onDialogChange={onDialogChange}
        onSimpleConfirm={onSimpleConfirm}
        onLifecycleConfirm={onLifecycleConfirm}
      />
    </div>
  );
}

function UserDetailDialogs({
  userName,
  simpleAction,
  lifecycleAction,
  onDialogChange,
  onSimpleConfirm,
  onLifecycleConfirm,
}: {
  readonly userName: string;
  readonly simpleAction: SimpleUserAction | null;
  readonly lifecycleAction: UserLifecycleAction | null;
  readonly onDialogChange: (dialog: UserAction | null) => void;
  readonly onSimpleConfirm: (reason: string) => void;
  readonly onLifecycleConfirm: (payload: UserLifecyclePayload) => void;
}): React.JSX.Element {
  return (
    <>
      <ConfirmDialog
        open={simpleAction !== null}
        title={simpleActionTitle(simpleAction)}
        description={simpleActionDescription(simpleAction)}
        reasonRequired
        reasonMinLength={10}
        onConfirm={onSimpleConfirm}
        onCancel={() => onDialogChange(null)}
      />
      <UserLifecycleDialog
        open={lifecycleAction !== null}
        action={lifecycleAction}
        userName={userName}
        onConfirm={onLifecycleConfirm}
        onCancel={() => onDialogChange(null)}
      />
    </>
  );
}

function isSimpleAction(action: UserAction | null): action is SimpleUserAction {
  return action === "freeze" || action === "unfreeze";
}

function isLifecycleAction(
  action: UserAction | null,
): action is UserLifecycleAction {
  return (
    action === "deactivate" ||
    action === "issue-warning" ||
    action === "suspend" ||
    action === "terminate"
  );
}

function simpleActionTitle(action: SimpleUserAction | null): string {
  return action === "freeze"
    ? t("user_detail.freeze_title")
    : t("user_detail.unfreeze_title");
}

function simpleActionDescription(action: SimpleUserAction | null): string {
  return action === "freeze"
    ? t("user_detail.freeze_desc")
    : t("user_detail.unfreeze_desc");
}
